<template>
  <ModalVue :modalActive="showModal" :hasHeaderBorder="true" :hasFooterBorder="true" @close="emit('cancel')">
    <template #header>
      <div class="flex flex-col text-left">
        <span class="font-bold text-lg text-textPrimary" data-testid="transition-modal-title">{{ title }}</span>
        <span v-if="contextLabel" class="text-xs text-[#68688D]">{{ contextLabel }}</span>
      </div>
    </template>

    <div v-if="isBlockedOnly" class="flex flex-col gap-3 text-left text-sm" data-testid="transition-blocked-only">
      <p>
        {{ blockedOnlyText }} Um plantão sem identificação nunca gera pagamento ao médico, então a troca
        não pode ser feita por aqui.
      </p>
      <template v-if="hasAntecipeBlock">
        <p class="font-bold">O que fazer:</p>
        <ol class="list-decimal pl-5 flex flex-col gap-1">
          <li>Confirme com o financeiro se o Antecipe foi de fato pago.</li>
          <li>Se o médico realizou o plantão, mantenha-o alocado.</li>
          <li>Se não realizou, trate o estorno antes de mudar a cobertura.</li>
        </ol>
      </template>
    </div>

    <div v-else class="flex flex-col gap-3 text-left text-sm">
      <p v-if="singleSentence" data-testid="transition-single-sentence">{{ singleSentence }}</p>
      <ul class="flex flex-col divide-y divide-gray-100 rounded-lg border border-solid border-lightGray30">
        <li class="flex justify-between gap-3 px-3 py-2" data-testid="transition-losing-doctor">
          <span>Plantões com médico que perdem o médico</span><b>{{ summary.losingDoctorCount }}</b>
        </li>
        <li v-if="notifiedCount > 0" class="flex justify-between gap-3 px-3 py-2" data-testid="transition-notified">
          <span>Médicos notificados<template v-if="summary.notifiedDoctorNames.length"> ({{ summary.notifiedDoctorNames.join(', ') }})</template></span>
          <b>{{ notifiedCount }}</b>
        </li>
        <li v-if="summary.losingDoctorCount > 0" class="flex justify-between gap-3 px-3 py-2" data-testid="transition-activity-requests">
          <span>Cobranças de Registro de Atividade canceladas</span><b>{{ formatOptional(summary.canceledActivityRequests) }}</b>
        </li>
        <li v-if="summary.losingDoctorCount > 0" class="flex justify-between gap-3 px-3 py-2" data-testid="transition-post-station">
          <span>Avaliações pós-plantão marcadas como obsoletas</span><b>{{ formatOptional(summary.obsoletedPostStationEvaluations) }}</b>
        </li>
        <li v-if="summary.losingDoctorCount > 0" class="flex justify-between gap-3 px-3 py-2" data-testid="transition-handover">
          <span>Convites de troca pendentes cancelados</span><b>{{ formatOptional(summary.canceledHandoverInvites) }}</b>
        </li>
        <li v-if="summary.openBecomingUnidentified > 0" class="flex justify-between gap-3 px-3 py-2" data-testid="transition-open-to-unidentified">
          <span>Vagas abertas que passam a ocupadas</span><b>{{ summary.openBecomingUnidentified }}</b>
        </li>
      </ul>
      <p
        v-if="summary.blocked.length"
        class="rounded-md border border-solid border-alert bg-alert/10 px-3 py-2"
        data-testid="transition-blocked"
      >{{ blockedText }}</p>
      <p class="text-xs text-[#68688D]">Nada será pago a médicos por esses plantões. O valor-base fica só como referência.</p>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse md:flex-row gap-3 justify-end w-full p-4">
        <OutlineButton height="48px" color="grey" data-testid="transition-cancel-button" @click="emit('cancel')">
          {{ isBlockedOnly ? 'Entendi' : 'Voltar' }}
        </OutlineButton>
        <FilledButton
          v-if="!isBlockedOnly"
          height="48px"
          color="primary"
          :loading="loading"
          data-testid="transition-confirm-button"
          @click="emit('confirm')"
        >
          Confirmar {{ summary.total }} {{ summary.total === 1 ? 'plantão' : 'plantões' }}
        </FilledButton>
      </div>
    </template>
  </ModalVue>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { BLOCKED_REASON_LABELS } from '@/modules/company/Dashboard/modals/utils/coverageAllocation'
import type { CoverageTransitionSummary } from '@/modules/company/Dashboard/modals/utils/coverageAllocation'

const props = withDefaults(
  defineProps<{
    showModal: boolean
    summary: CoverageTransitionSummary
    contextLabel?: string | null
    responsibleName?: string | null
    loading?: boolean
  }>(),
  { contextLabel: null, responsibleName: null, loading: false }
)

const emit = defineEmits<{ confirm: []; cancel: [] }>()

const isBlockedOnly = computed(() => props.summary.total === 0 && props.summary.blocked.length > 0)
const hasAntecipeBlock = computed(() => props.summary.blocked.some((b) => b.reason === 'ANTECIPE'))
const notifiedCount = computed(() =>
  Math.max(props.summary.notifiedDoctorNames.length, props.summary.losingDoctorCount > 0 ? 1 : 0)
)

const title = computed(() => {
  if (isBlockedOnly.value) return 'Não é possível marcar sem identificação'
  const n = props.summary.total
  return n === 1
    ? 'Marcar 1 plantão como ocupado sem identificação?'
    : `Marcar ${n} plantões como ocupados sem identificação?`
})

const singleSentence = computed(() => {
  const name = props.summary.notifiedDoctorNames[0]
  if (props.summary.total !== 1 || props.summary.losingDoctorCount !== 1 || !name) return null
  const responsible = props.responsibleName || 'o responsável selecionado'
  return `O plantão deixa de ser de ${name} e passa a ser coberto por ${responsible}. ${name} será notificado de que foi removido.`
})

const blockedText = computed(() => {
  const list = props.summary.blocked
  const detail = list.map((b) => `${b.date}, com ${BLOCKED_REASON_LABELS[b.reason]}`).join('; ')
  const head = list.length === 1 ? '1 plantão bloqueado e ignorado' : `${list.length} plantões bloqueados e ignorados`
  const tail = hasAntecipeBlock.value
    ? ` Para ${list.length === 1 ? 'alterá-lo' : 'alterá-los'}, é preciso antes tratar o Antecipe com o financeiro.`
    : ''
  return `${head}: ${detail}.${tail}`
})

const blockedOnlyText = computed(() => {
  const first = props.summary.blocked[0]
  if (!first) return ''
  return `Este plantão tem ${BLOCKED_REASON_LABELS[first.reason]}.`
})

const formatOptional = (value: number | null) => (value === null ? 'se houver' : String(value))
</script>
